/**
 * The typed client. Every response the app reads goes through here.
 *
 * Two jobs. It turns the server's error envelope into something the queue can
 * branch on - `retryable` comes from the server rather than being guessed from
 * the status code, because `rate_limited` is a 4xx that must be retried and any
 * is-it-4xx heuristic gets that wrong.
 *
 * And it validates `summary.json` against the generated contract on arrival.
 * That is deliberate: BUILD-SPEC 2 says the app formats and filters but never
 * derives, so if a field it displays is missing the honest failure is at the
 * fetch, once, with a clear message - not fifteen `undefined`s rendered as
 * "NaN" across a screen.
 */
import { SummarySchema, type Summary } from "./contracts/index.js";
import { photoBlob, type QueueItem, type UploadResult } from "./queue.js";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly retryable: boolean,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function envelope(res: Response): Promise<ApiError> {
  let code = "internal";
  let message = res.statusText || `HTTP ${res.status}`;
  let retryable = res.status >= 500;
  let details: Record<string, unknown> | undefined;
  try {
    const body = (await res.json()) as { error?: Record<string, unknown> };
    if (body.error) {
      code = String(body.error.code ?? code);
      message = String(body.error.message ?? message);
      retryable = Boolean(body.error.retryable ?? retryable);
      details = body.error.details as Record<string, unknown> | undefined;
    }
  } catch {
    /* not JSON: keep the status-derived defaults */
  }
  return new ApiError(res.status, code, message, retryable, details);
}

/** The device token, minted at login. Held in memory plus localStorage: the
 *  session cookie covers the foreground, this covers a reload and any call the
 *  service worker might make. */
const TOKEN_KEY = "grk_device_token";

export function setToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

function authHeaders(extra: HeadersInit = {}): HeadersInit {
  const token = getToken();
  return token ? { ...extra, authorization: `Bearer ${token}` } : extra;
}

async function json<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, { ...init, headers: authHeaders(init.headers) });
  if (!res.ok) throw await envelope(res);
  return (await res.json()) as T;
}

// --------------------------------------------------------------------------

export interface Account {
  id: string;
  username: string;
  timezone: string;
  must_change_password: boolean;
}

export async function login(username: string, password: string): Promise<Account> {
  const res = await fetch("/api/login", {
    method: "POST",
    body: new URLSearchParams({ username, password, device_label: deviceLabel() }),
  });
  if (!res.ok) throw await envelope(res);
  const body = (await res.json()) as { account: Account; device_token: string };
  setToken(body.device_token);
  return body.account;
}

export async function logout(): Promise<void> {
  try {
    await fetch("/api/logout", { method: "POST", headers: authHeaders() });
  } finally {
    setToken(null);
  }
}

export function whoami(): Promise<{ account_id: string; username: string; timezone: string }> {
  return json("/api/whoami");
}

export function changePassword(newPassword: string): Promise<{ ok: true }> {
  return json("/api/password", {
    method: "POST",
    body: new URLSearchParams({ new_password: newPassword }),
  });
}

function deviceLabel(): string {
  const ua = navigator.userAgent;
  if (/iPhone|iPad/.test(ua)) return "iPhone";
  if (/Android/.test(ua)) return "Android";
  return "browser";
}

// --------------------------------------------------------------------------

export interface CaptureRow {
  capture_id: string;
  status: "pending" | "held" | "processed" | "discarded";
  captured_at: string;
  store: string;
  card_total: number | null;
  currency: string;
  page_of: string | null;
  note: string | null;
  /** False once the server has pruned the photo (after processing). */
  photo_available?: boolean;
  answers: { question: string; answer: string | null; answered_at: string | null }[];
}

export interface QueueView {
  queue: { pending: number; oldest_pending: string | null };
  captures: CaptureRow[];
}

export function captures(status?: string): Promise<QueueView> {
  return json(`/api/captures${status ? `?status=${encodeURIComponent(status)}` : ""}`);
}

export function answerCapture(captureId: string, answer: string): Promise<unknown> {
  return json(`/api/captures/${encodeURIComponent(captureId)}/answer`, {
    method: "POST",
    body: new URLSearchParams({ answer }),
  });
}

export function discardCapture(captureId: string): Promise<unknown> {
  return json(`/api/captures/${encodeURIComponent(captureId)}`, { method: "DELETE" });
}

/** Add or correct the terminal figure while a capture is pending/held - the
 *  second chance the till screen deliberately doesn't force. */
export function updateCaptureTotal(
  captureId: string,
  cardTotal: number | null,
): Promise<{ capture_id: string; card_total: number | null }> {
  const body = new URLSearchParams();
  body.set("card_total", cardTotal === null ? "" : String(cardTotal));
  return json(`/api/captures/${encodeURIComponent(captureId)}`, { method: "PATCH", body });
}

export function config(): Promise<{ config: { stores?: string[] }; rules_md: string }> {
  return json("/api/config");
}

/**
 * Equivalence-group decisions. Made here, stored on the server, applied to
 * equivalents.csv by fetch.py at the next run - so a decision recorded now
 * shows its effect one publish later, and the UI says exactly that.
 */
export interface EquivalentEdit {
  item_a: string;
  store_a: string;
  item_b: string;
  store_b: string;
  status: "accepted" | "rejected";
  relation?: "same" | "similar";
  confidence?: number;
  note?: string;
  decided_at?: string;
}

export function equivalentEdits(): Promise<{ edits: EquivalentEdit[] }> {
  return json("/api/equivalents");
}

/** Product-identity decisions (Review tab). Same lifecycle as equivalents:
 *  recorded here, applied on the PC at the next fetch, visible next publish. */
export interface ProductMetaEdit {
  product: string;
  status: "confirmed" | "rejected";
  barcode?: string | null;
  off_name?: string | null;
  size_qty?: number | null;
  size_unit?: string | null;
  decided_at?: string;
}

export function productMetaEdits(): Promise<{ edits: ProductMetaEdit[] }> {
  return json("/api/product-meta");
}

export function saveProductMetaEdit(edit: ProductMetaEdit): Promise<{ edits: ProductMetaEdit[] }> {
  const body = new URLSearchParams();
  body.set("product", edit.product);
  body.set("status", edit.status);
  if (edit.barcode) body.set("barcode", edit.barcode);
  if (edit.off_name) body.set("off_name", edit.off_name);
  if (edit.size_qty !== undefined && edit.size_qty !== null) body.set("size_qty", String(edit.size_qty));
  if (edit.size_unit) body.set("size_unit", edit.size_unit);
  return json("/api/product-meta", { method: "POST", body });
}

export function saveEquivalentEdit(edit: EquivalentEdit): Promise<{ edits: EquivalentEdit[] }> {
  const body = new URLSearchParams();
  body.set("item_a", edit.item_a);
  body.set("store_a", edit.store_a);
  body.set("item_b", edit.item_b);
  body.set("store_b", edit.store_b);
  body.set("status", edit.status);
  if (edit.relation) body.set("relation", edit.relation);
  if (edit.confidence !== undefined) body.set("confidence", String(edit.confidence));
  if (edit.note) body.set("note", edit.note);
  return json("/api/equivalents", { method: "POST", body });
}

export function runs(): Promise<{
  runs: {
    run_id: string;
    published_at: string;
    marked_processed: number;
    marked_held: number;
    requeued: number;
  }[];
}> {
  return json("/api/runs");
}

/**
 * Fetch and validate the published summary, conditionally.
 *
 * The server content-addresses the artifact, so an `If-None-Match` with the
 * cached ETag turns the everyday case - nothing published since last time -
 * into a 304 with no body. `none` means nothing has been published yet, a
 * normal state before the first run rather than an error.
 */
export interface SummaryFetchResult {
  status: "ok" | "unchanged" | "none";
  summary?: Summary;
  etag?: string | null;
}

export async function summaryConditional(etag: string | null = null): Promise<SummaryFetchResult> {
  const extra: Record<string, string> = {};
  if (etag) extra["if-none-match"] = etag;
  const res = await fetch("/api/view/summary.json", { headers: authHeaders(extra) });
  if (res.status === 304) return { status: "unchanged" };
  if (res.status === 404) return { status: "none" };
  if (!res.ok) throw await envelope(res);
  const parsed = SummarySchema.safeParse(await res.json());
  if (!parsed.success) {
    // Loud and once, rather than NaN fifteen times across a screen.
    throw new ApiError(
      200,
      "schema_unsupported",
      `summary.json does not match the contract this app was built against: ` +
        parsed.error.issues
          .slice(0, 3)
          .map((i) => `${i.path.join(".")} ${i.message}`)
          .join("; "),
      false,
    );
  }
  return { status: "ok", summary: parsed.data, etag: res.headers.get("etag") };
}

/** The unconditional form, kept for callers that don't hold an ETag. */
export async function summary(): Promise<Summary | null> {
  const res = await summaryConditional(null);
  return res.status === "ok" ? (res.summary ?? null) : null;
}

/**
 * A capture's photo, as bytes. Used by the view cache (`store.ts`), which owns
 * the Cache API side of it; this is just the authenticated fetch.
 */
export async function capturePhoto(captureId: string): Promise<Blob> {
  const res = await fetch(`/api/captures/${encodeURIComponent(captureId)}/photo`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw await envelope(res);
  return res.blob();
}

/**
 * Download a published artifact - the on-demand export from BUILD-SPEC 8.
 * `Grocery Spend.xlsx` survives as this, not as the way you look at data.
 *
 * Fetched rather than linked, because the request needs the Authorization
 * header and an <a href> cannot carry one.
 */
export async function downloadArtifact(name: string): Promise<void> {
  const res = await fetch(`/api/view/${encodeURIComponent(name)}`, { headers: authHeaders() });
  if (!res.ok) throw await envelope(res);
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = name === "spend.xlsx" ? "Grocery Spend.xlsx" : name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Delayed: revoking synchronously races the click on some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

// --------------------------------------------------------------------------

/** The uploader the queue drives. Kept here so `queue.ts` stays free of
 *  transport concerns and can be tested without a server. */
export async function uploadCapture(item: QueueItem): Promise<UploadResult> {
  const form = new FormData();
  form.set("photo", photoBlob(item), `${item.capture_id}.jpg`);
  form.set("sidecar", JSON.stringify(item.sidecar));

  const res = await fetch("/api/captures", {
    method: "POST",
    headers: authHeaders(),
    body: form,
  });

  if (res.ok) {
    const body = (await res.json()) as { duplicate?: boolean };
    return { ok: true, status: res.status, duplicate: body.duplicate === true, retryable: false };
  }
  const err = await envelope(res);
  return {
    ok: false,
    status: err.status,
    code: err.code,
    message: err.message,
    retryable: err.retryable,
  };
}
